/* eslint-disable */
import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import SearchIcon from '../../assets/icons/search.svg';
import SortIcon from '../../assets/icons/sort.svg';
import './RecentChanges.scss';
import api, { apiWithRetry } from '../../utils/api';

interface ChangeItem {
  id: number;
  user: string;
  userAvatar: string;
  action: string;
  detail: string;
  updated: string;
  isComment?: boolean;
}

interface RecentChangesProps {
  onExpandChange?: (expanded: boolean) => void;
}

const RecentChanges: React.FC<RecentChangesProps> = ({ onExpandChange }) => {

  const [expanded, setExpanded] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(true);

  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    hasMore: true
  });

  const [limit] = useState(50);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'newest' | 'oldest' | 'month'>('newest');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const listRef = useRef<HTMLDivElement | null>(null);

  const handleToggleExpand = () => {
    const newState = !expanded;
    setExpanded(newState);

    if (newState && changes.length <= 2) {
      setPagination({ page: 1, hasMore: true });
      fetchRecentChanges(1, true);
    }

    onExpandChange?.(newState);
  };

  const fetchRecentChanges = async (
    pageNumber = 1,
    reset = false,
    sortValue = sort
  ) => {

    if (loading) return;

    try {

      setLoading(true);

      const res = await apiWithRetry(() =>
        api.get('/fec/recent-updates', {
          params: {
            page: pageNumber,
            limit,
            sort: sortValue,
            search: debouncedSearch
          }
        })
      );

      if (res?.data?.success) {

        const apiData = res.data.data?.data || [];
        const totalPages = res.data.data?.totalPages || 1;

        const mapped = apiData.map((item: any) => ({
          id: item.id,
          user: item.userName,
          userAvatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(item.userName)}&background=2D3748&color=fff&rounded=true&size=40`,
          action: item.comment ? 'Commented' : 'Changed data in',
          detail: item.comment
            ? `"${item.comment}"`
            : `${item.metric}: ${item.oldValue ?? '-'} → ${item.newValue}`,
          updated: new Date(item.timestamp).toLocaleString(),
          isComment: !!item.comment
        }));

        if (reset) {
          setChanges(mapped);
        } else {
          setChanges(prev => [...prev, ...mapped]);
        }

        setPagination({
          page: pageNumber,
          hasMore: pageNumber < totalPages
        });

      }

    } catch (err) {
      console.error('Recent updates API error:', err);
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    fetchRecentChanges(1, true);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPagination({ page: 1, hasMore: true });
    fetchRecentChanges(1, true);
  }, [debouncedSearch]);

  const loadMore = () => {
    if (!pagination.hasMore || loading) return;
    const nextPage = pagination.page + 1;

    setPagination(prev => ({
      ...prev,
      page: nextPage
    }));

    fetchRecentChanges(nextPage);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (
      !loading &&
      pagination.hasMore &&
      target.scrollHeight - target.scrollTop <= target.clientHeight + 80
    ) {
      loadMore();
    }
  };

  /* auto fetch if container not filled */
  useEffect(() => {
    if (!expanded) return;

    const el = listRef.current;
    if (!el || loading || !pagination.hasMore) return;

    if (el.scrollHeight <= el.clientHeight) {
      loadMore();
    }
  }, [changes]);

  // Show only 2 items when collapsed
  const visibleChanges = expanded ? changes : changes.slice(0, 2);

  return (
    <div className={`recent-changes-container`}>

      <div className="recent-changes-header">

        <h3>Recent Changes</h3>

        <div className="header-icons">

          <img
            src={SearchIcon}
            alt="Search"
            className="icon"
            onClick={() => setShowSearch(prev => !prev)}
          />

          <img
            src={SortIcon}
            alt="Sort"
            className="icon"
            onClick={() => setSortOpen(!sortOpen)}
          />

          {changes.length > 2 && (
            <i
              className={`pi ${auditExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
              onClick={() => setAuditExpanded(!auditExpanded)}
            />
          )}

        </div>

      </div>

      {sortOpen && (
        <div className="sort-dropdown">
          <div
            onClick={() => {
              setSortOpen(false);
              setSort('newest');
              setPagination({ page: 1, hasMore: true });
              fetchRecentChanges(1, true, 'newest');
            }}
          >
            Newer to Older
          </div>
          <div
            onClick={() => {
              setSortOpen(false);
              setSort('oldest');
              setPagination({ page: 1, hasMore: true });
              fetchRecentChanges(1, true, 'oldest');
            }}
          >
            Older to Newer
          </div>
          <div
            onClick={() => {
              setSortOpen(false);
              setSort('month');
              setPagination({ page: 1, hasMore: true });
              fetchRecentChanges(1, true, 'month');
            }}
          >
            Order by Month
          </div>
        </div>
      )}

      {showSearch && (
        <div className="search-box">
          <input
            placeholder="Search changes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {auditExpanded && (
        <div
          ref={listRef}
          className="recent-changes-list"
          style={expanded ? { maxHeight: '100vh', overflowY: 'auto' } : {}}
          onScroll={expanded ? handleScroll : undefined}
        >
          {visibleChanges.map((change) => (
            <div key={change.id} className="change-card">
              <div className="user-info">
                <img
                  src={change.userAvatar}
                  alt={change.user}
                  className="avatar"
                />
                <div className="details">
                  <span className="username">
                    {change.user}
                  </span>
                  <p className="action">
                    {change.isComment ? (
                      <>
                        Commented <span className="highlight">{change.detail}</span>
                      </>
                    ) : (
                      <>
                        {change.action} <span className="highlight">{change.detail}</span>
                      </>
                    )}
                  </p>
                  <span className="timestamp">
                    {change.updated}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="recent-loader">
              Loading...
            </div>
          )}
        </div>
      )}

      {changes.length > 2 && (
        <Button
          label={expanded ? 'Collapse' : 'View All'}
          className="p-button-outlined view-all-btn"
          onClick={handleToggleExpand}
        />
      )}

    </div>
  );
};

export default RecentChanges;